import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEventType } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { RequestService } from './request.service';
import { LayoutUtilsService } from './utils/layout-utils.service';
// import { VonageService } from './vonage.service';
import { MatDialog } from '@angular/material/dialog';
import { LoaderService } from './loader.service';
import { ChatService } from './chat.service';
import { NotificationService } from './notification.service';
import { Router } from '@angular/router';
import { openCenteredPopup, getResponsiveDialogWidth } from '../helpers';
import { LogsInvestigatorMapDialogComponent } from 'src/app/pages/case-page/investigator/logs-investigator-dialog/logs-investigator-dialog.component';
import { MessagingService } from './messaging.service';
import { DeviceIdService } from './device-id.service';
import { NotificationStorageService } from './notification-storage.service';
import { environment } from 'src/environments/environment';
import { CaseService } from './case.service';
import { getOrganizationTemplateDisplayName, resolveTemplateImageSrc } from '../constants/template-options';
import { buildChatNotificationUrl } from '../utils/chat-notification-url';
import { ChatOpenRegistryService } from './chat-open-registry.service';
import { shouldBypassDnd } from '../utils/dnd-bypass';
import { previewChatContent } from '../utils/chat-content';

@Injectable({
  providedIn: 'root'
})
export class NotificationsHandlersService {

  public isPopUpOpener: boolean = false;
  public clientInstanceId: string = '';
  currentMessage = new BehaviorSubject(null);
  token: string = '';
  progressRef: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public connectionStatus: BehaviorSubject<'connected' | 'disconnected' | 'connecting'> = new BehaviorSubject<'connected' | 'disconnected' | 'connecting'>('disconnected');

  private isRegistered: boolean = false;
  private natsSubscriptionActive: boolean = false;
  /** Single promise for in-flight init; multiple callers await this so init runs only once per session. */
  private natsInitPromise: Promise<void> | null = null;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private recentNotificationIds: Map<string, number> = new Map();
  private activeCallInviteNotifications: Set<Notification> = new Set<Notification>();
  private pendingReadNotificationTargets: Set<string> = new Set<string>();
  iconImageSrc: any;
  badgeImageSrc: any;
  private get appTitle(): string {
    return getOrganizationTemplateDisplayName(this.requestService.pageOrganization.getValue());
  }

  constructor(
    private http: HttpClient,
    private requestService: RequestService,
    private translate: TranslateService,
    private layoutUtilsService: LayoutUtilsService,
    // private vonageService: VonageService,
    private dialog: MatDialog,
    private loader: LoaderService,
    private chatService: ChatService,
    private notificationService: NotificationService,
    private router: Router,
    private natsMessaging: MessagingService,
    private deviceIdService: DeviceIdService,
    private notificationStorage: NotificationStorageService,
    private caseService: CaseService,
    private chatOpenRegistry: ChatOpenRegistryService
  ) {
    this.isPopUpOpener = window.opener ? true : false;

    const existingClientInstanceId = sessionStorage.getItem('clientInstanceId') || '';
    this.clientInstanceId = existingClientInstanceId || (globalThis.crypto && 'randomUUID' in globalThis.crypto ? (globalThis.crypto as any).randomUUID() : `${Date.now()}_${Math.random()}`);
    sessionStorage.setItem('clientInstanceId', this.clientInstanceId);

    // Set the device token from DeviceIdService
    this.token = this.deviceIdService.getDeviceId();
    this.tokenSubject.next(this.token);

    // Subscribe to NATS connection status
    this.natsMessaging.isConnected.subscribe(connected => {
      this.connectionStatus.next(connected ? 'connected' : 'disconnected');
    });

    // Register service worker for background notifications
    this.registerServiceWorker();

    // Request notification permission
    this.requestNotificationPermission();

    // Initialize NATS subscription for current user
    this.initializeNatsSubscription();
    this.requestService.currentUserSubject.subscribe((user) => {
      if (user?._id && this.pendingReadNotificationTargets.size) {
        Array.from(this.pendingReadNotificationTargets).forEach((target) => {
          this.markClickedNotificationAsRead(target);
        });
      }

      if (user && user._id && !this.natsSubscriptionActive) {
        this.initializeNatsSubscription();
      }
    });

    if ('serviceWorker' in navigator && !this.isPopUpOpener) {
      navigator.serviceWorker.addEventListener('message', (event: any) => {
        console.log('event fb', event);
        if (event.data?.notificationId) {
          this.markClickedNotificationAsRead(event.data.notificationId);
        }

        if (event.data?.type === 'PUSH_NOTIFICATION') {
          const payload = event.data.data;
          if ((payload.data?.invitedBy != this.requestService.currentUser?._id && payload.data?.invitedBy != this.chatService.ongoingCall) || payload.data.type != 'call') {
            this.currentMessage.next(payload);
          }
          return;
        }
        const isCallInvite = event.data?.type === 'call'
          && event.data?.callSessionId
          && (event.data?.callType === 'calls' || event.data?.callType === 'invite' || !event.data?.callType)
          && !event.data?.status
          && !event.data?.event
          && event.data?.isCall !== false
          && !event.data?.isCallAccepted
          && !event.data?.isCallJoined;

        if (this.isCallInviteTerminalEvent(event.data)) {
          this.closeCallInviteNotifications(event.data?.callSessionId);
        }

        if (event.data?.invitedBy && isCallInvite) {
          let data = event.data;
          data['show'] = true;
          if (this.chatService.ongoingCall) {
            // User is already in a call, show a foreground notification instead of the call prompt
            this.layoutUtilsService.showNotification(
              this.translate.instant('You are already in a call. Cannot join another call.'),
              this.translate.instant('Dismiss')
            );
          } else {
            this.chatService.showCallPromptSubject.next(data);
          }
        }
        else if (event.data.action == 'case') {
          if (this.checkIfSameCase(event.data.redirect)) {
            this.router.navigateByUrl(event.data.redirect);
          } else {
            this.router.navigate(['cases/list']).then(() => {
              setTimeout(() => {
                this.router.navigateByUrl(event.data.redirect);
              }, 500);
            });
          }
        }
        else if (event.data.action == 'chat') {
          if (this.checkIfSameCase(event.data.redirect)) {
            this.router.navigateByUrl(event.data.redirect);
          } else {
            this.router.navigate(['cases/list']).then(() => {
              setTimeout(() => {
                this.router.navigateByUrl(event.data.redirect);
              }, 500);
            });
          }
        }
        else if (event.data.action == 'investigator-chat') {
          let url = '/global-chat/' + event.data.senderId;
          // window.open(url);
          if (window.opener) {
            this.router.navigateByUrl(url);
          } else {
            openCenteredPopup(url, this.translate.instant('investigator') + ':' + event.data.senderName);
          }
          // this.router.navigate(['/notifications']).then(() => {
          //   // this.loader.display(true);
          //   // this.getUserToken(event.data.senderId).then((userTokens: any) => {
          //   // if (userTokens.length) {
          //   this.chatService.showChatDialog(event.data.senderId, event.data.senderName, true);
          //   // }
          //   // this.loader.display(false);
          //   // }).catch((e) => {
          //   // this.loader.display(false);
          //   // });
          // });

        } else if (event.data.action == 'investigator-notifications') {
          this.notificationService.refreshData.next(true);
          if (window.opener) {
            this.router.navigateByUrl('/notifications');
          } else {
            if (window.location.pathname.indexOf('/notifications') > -1 && window.location.pathname.indexOf('/case') == -1) {
              // do nothing
            } else {
              openCenteredPopup('/notifications', this.translate.instant('Notifications'));
            }
          }
        }
        else if (event.data.action == 'missedcall') {
          if (window.opener) {
            this.router.navigateByUrl('/cases/list');
          } else {
            const dialogRef = this.dialog.open(LogsInvestigatorMapDialogComponent, {
              autoFocus: false,
              width: getResponsiveDialogWidth(),
              data: {
                caseId: '',
                userId: event.data.senderId,
                title: this.translate.instant('Call Log'),
              }
            });
            dialogRef.afterClosed().subscribe(result => {

            });
          }
        }
        else if (event.data.action == 'threat') {
          const threatUrl = event.data.redirect;
          if (!threatUrl) {
            return;
          }
          if (window.opener) {
            this.router.navigateByUrl(threatUrl);
          } else {
            openCenteredPopup(threatUrl, this.translate.instant('Detection Management Center'));
          }
        }
      });
    }
    void resolveTemplateImageSrc('logo-small.png', 'assets/images/logo-small.png', this.requestService.pageOrganization.getValue())
      .then((src) => {
        this.iconImageSrc = src;
        this.badgeImageSrc = src;
      });

    const openedNotificationId = new URLSearchParams(window.location.search).get('notificationId');
    if (openedNotificationId) {
      this.markClickedNotificationAsRead(openedNotificationId);
    }
  }

  public markClickedNotificationAsRead(notificationOrTarget?: any): void {
    const data = typeof notificationOrTarget === 'object'
      ? (notificationOrTarget?.data || notificationOrTarget)
      : undefined;
    const type = data?.type || notificationOrTarget?.type;

    // Chat unread state is managed by ChatService, while call events are not
    // part of the notification unread groups.
    if (['chat', 'call', 'call-invite', 'call-canceled', 'missedcall'].includes(type)) return;

    const explicitTarget = typeof notificationOrTarget === 'string' ? notificationOrTarget : '';
    const notificationId = data?.notificationId
      || notificationOrTarget?.notificationId
      || data?._id;
    const caseId = data?.notificationCaseId
      || notificationOrTarget?.notificationCaseId
      || data?.caseId
      || data?.case?._id;
    const readTarget = explicitTarget || (notificationId ? String(notificationId) : (caseId ? `case:${caseId}` : ''));

    if (!readTarget) return;

    const userId = this.requestService.currentUser?._id || this.requestService.getUserId();
    if (!userId) {
      this.pendingReadNotificationTargets.add(readTarget);
      return;
    }

    this.pendingReadNotificationTargets.delete(readTarget);
    if (readTarget.startsWith('case:')) {
      this.notificationService.markAsRead(userId, { caseId: readTarget.substring('case:'.length) });
    } else {
      this.notificationService.markAsRead(userId, { notificationId: readTarget });
    }
    this.notificationService.refreshData.next(true);
  }

  checkIfSameCase(sentPath: string): boolean {
    const currentUrlPath = this.router.url.split('?')[0];
    const inputPath = sentPath.split('?')[0];
    return currentUrlPath === inputPath;
  }

  // Helper function to check if we're in a secure context (HTTPS or localhost)
  private isSecureContext(): boolean {
    return window.isSecureContext ||
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
  }

  /**
   * Build all chat dedupe keys for a notification-grade payload.
   * Dual publishes often disagree on shape (clientMessageId present vs absent,
   * text in content vs body). Registering every key means either form of the
   * second arrival still matches.
   */
  private getChatDedupKeys(data: any): string[] {
    const keys: string[] = [];
    if (data?.clientMessageId) {
      keys.push(`chat_msg_${data.clientMessageId}`);
    }
    const sender = data?.senderId || '';
    const receiver = data?.receiverId || data?.groupId || data?.caseId || '';
    const msg = String(data?.content || data?.message || data?.body || '').substring(0, 64);
    const file = data?.fileName || '';
    keys.push(`chat_${sender}_${receiver}_${msg}_${file}`);
    // Case-scoped key so Message All fanout with differing receiverIds still collapses.
    if (data?.caseId) {
      keys.push(`chat_case_${sender}_${data.caseId}_${msg}_${file}`);
    }
    return keys;
  }

  /**
   * Deduplicate incoming notifications (by message id or content fingerprint).
   * Group/general chat messages may arrive twice on the same NATS subject
   * (once from _sendPushNotifications and once from sendPushBatch) without
   * an _id field. We generate a fingerprint from the message content so
   * the second arrival is correctly deduplicated.
   */
  private shouldProcessNotification(payload: any): boolean {
    const data = payload?.data || payload;
    const type = data?.type || '';

    // Realtime chat sync (no notificationGrade) must not block the push-batch arrival.
    if (type === 'chat' && !data?.notificationGrade && !data?.cipher_timestamp) {
      return true;
    }

    const now = Date.now();

    if (type === 'chat') {
      const text = String(data?.content || data?.message || data?.body || '').trim();
      const file = String(data?.fileName || '').trim();
      // Empty notification-grade shells must not poison dedupe — a fuller twin often follows.
      if (!text && !file) {
        return true;
      }

      const keys = this.getChatDedupKeys(data);
      for (const id of keys) {
        const lastSeen = this.recentNotificationIds.get(id);
        if (lastSeen && now - lastSeen < 60000) {
          return false;
        }
      }
      for (const id of keys) {
        this.recentNotificationIds.set(id, now);
      }
      for (const [key, ts] of this.recentNotificationIds.entries()) {
        if (now - ts > 5 * 60 * 1000) {
          this.recentNotificationIds.delete(key);
        }
      }
      return true;
    }

    const id = data?._id || data?.id;
    if (!id) return true;

    // Decline and acceptance events intentionally share the call session id.
    // Keep acceptance in its own dedupe bucket so a preceding decline cannot
    // suppress the event that tells the caller to join the room.
    const isCallAcceptance = type === 'call-invite'
      && (!!data?.isCallAccepted || !!data?.isCallJoined);
    const deduplicationId = isCallAcceptance ? `${id}:accepted` : id;
    const lastSeen = this.recentNotificationIds.get(deduplicationId);
    // Call invites may reuse identifiers between retries; keep a short dedup window
    // to suppress true duplicates while allowing immediate re-invites.
    const dedupWindowMs = type === 'call' ? 1500 : 60000;
    if (lastSeen && now - lastSeen < dedupWindowMs && type !== 'missedcall' && type !== 'call-canceled' && data.callType !== 'invite') { // added this to prevent missedcall from being blocked because the call notify has same id
      return false;
    }

    this.recentNotificationIds.set(deduplicationId, now);
    // Clean up old entries (5 minutes)
    for (const [key, ts] of this.recentNotificationIds.entries()) {
      if (now - ts > 5 * 60 * 1000) {
        this.recentNotificationIds.delete(key);
      }
    }
    return true;
  }

  private isCodeVersionUpdateFromSelf(payload: any): boolean {
    const data = payload?.data || payload || {};
    const action = String(data?.action || '').toLowerCase();
    const isCodeVersion =
      action === 'code-version-update';
    if (!isCodeVersion) {
      return false;
    }
    const publishedBy = data?.publishedByUserId;
    const currentUserId = this.requestService.currentUser?._id;
    if (!publishedBy || !currentUserId) {
      return false;
    }
    return String(publishedBy) === String(currentUserId);
  }

  private isIncomingCallInvite(data: any): boolean {
    return data?.type === 'call'
      && !!data?.callSessionId
      && (data?.callType === 'calls' || data?.callType === 'invite' || !data?.callType)
      && !data?.status
      && !data?.event
      && data?.isCall !== false
      && !data?.isCallAccepted
      && !data?.isCallJoined;
  }

  private isCallInviteTerminalEvent(data: any): boolean {
    if (!data) return false;
    if (data?.type === 'missedcall' || data?.type === 'call-canceled') return true;
    if (data?.type !== 'call') return false;
    return ['declined', 'busy', 'ended', 'not-answered'].includes(data?.status)
      || !!data?.isCallAccepted
      || !!data?.isCallJoined;
  }

  public async closeCallInviteNotifications(callSessionId?: string): Promise<void> {
    const matchesSession = (data: any): boolean => {
      if (!callSessionId) return true;
      return !!data?.callSessionId && String(data.callSessionId) === String(callSessionId);
    };

    this.activeCallInviteNotifications.forEach((notification) => {
      const nData: any = (notification as any)?.data || {};
      if (nData?.type === 'call' && this.isIncomingCallInvite(nData) && matchesSession(nData)) {
        notification.close();
        this.activeCallInviteNotifications.delete(notification);
      }
    });

    if (!this.swRegistration) return;
    try {
      this.swRegistration.active?.postMessage({
        type: 'CLOSE_CALL_INVITE_NOTIFICATIONS',
        callSessionId: callSessionId || ''
      });
      const notifications = await this.swRegistration.getNotifications();
      notifications.forEach((notification: any) => {
        const nData = notification?.data?.MQTT_MSG?.data || notification?.data || {};
        if (nData?.type === 'call' && this.isIncomingCallInvite(nData) && matchesSession(nData)) {
          notification.close();
        }
      });
    } catch {
      // ignore close failures
    }
  }

  /**
   * Handle incoming NATS messages for notifications
   */
  private async handleNatsMessage(payload: any): Promise<void> {
    if (this.isCodeVersionUpdateFromSelf(payload)) {
      return;
    }

    if (!this.shouldProcessNotification(payload)) {
      return;
    }

    payload = this.normalizeIncomingPayload(payload);
    // console.log('[NotificationsHandlers] Received NATS message:', payload);

    // Store notification for offline access
    try {
      await this.storeNotification(payload);
    } catch {
      // Ignore storage errors
    }

    const data = payload?.data || payload;
    const type = payload?.type || data?.type;
    const senderId = data?.senderId;
    const currentUserId = this.requestService.currentUser?._id;

    if (this.isCallInviteTerminalEvent(data)) {
      await this.closeCallInviteNotifications(data?.callSessionId);
    }

    // Skip notifications from self for browser notifications
    const isFromSelf = senderId === currentUserId;
    if (isFromSelf && type !== 'call' && type !== 'chat' && type !== 'case') {
      return;
    }

    // For calls, check if it's from someone else and not already in a call
    const isCallNotification = type === 'call';
    const shouldSkipCall = isCallNotification && (
      data?.invitedBy === currentUserId ||
      data?.invitedBy === this.chatService.ongoingCall
    );

    const isAnsweredCallMissedEvent = type === 'missedcall' && (
      !!data?.isCallAccepted ||
      !!data?.isCallJoined ||
      data?.status === 'ended' ||
      data?.event === 'ended' ||
      String(data?.endedBy || '') === String(currentUserId || '') ||
      String(data?.closedBy || '') === String(currentUserId || '')
    );

    if (shouldSkipCall || isAnsweredCallMissedEvent) {
      // console.log('[NotificationsHandlers] Skipping call notification (self or ongoing call)');
      return;
    }

    // DND policy (#1047): when the receiver has DND ON, suppress both the
    // in-app banner AND the background system notification for everything
    // except call invites and missed-call signals. Unread badge counters
    // must still update so the user sees activity when they open the app.
    const isDnd = !!this.notificationService.dnd.getValue();
    // When the outer type is a generic envelope ('notify'), also check the inner
    // data.type so that call-control signals (e.g. call-invite/isCallAccepted sent
    // to the caller) bypass DND regardless of the envelope type (#1047).
    const innerType = (type === 'notify' && data?.type) ? data.type : type;
    const bypassDnd = shouldBypassDnd(innerType, data);
    const dndSuppress = isDnd && !bypassDnd;

    if (type === 'chat' && !isFromSelf && this.chatOpenRegistry.isSuppressed(data)) {
      return;
    }

    // Always update unread state regardless of DND — the badge counter is
    // expected to keep ticking so the user can see what they missed.
    if (type === 'notify' && !isFromSelf) {
      this.notificationService.getUnreadNotifications();
    }

    // Update unread count for chat messages.
    // `type` is already resolved from payload.type || data.type, so we use it
    // directly instead of checking payload?.type (which may be undefined when
    // the message arrives from the _sendPushNotifications NATS publish that
    // omits a top-level type field).
    // General chat messages (isGeneralChat) are also included so that group
    // unread state is tracked correctly and survives a page refresh.
    if (type === 'chat' && !isFromSelf && !data?.notificationGrade && !data?.cipher_timestamp) {
      this.chatService.handleNewMessage(data);
    }

    if (dndSuppress) {
      // console.log('[NotificationsHandlers] DND on — suppressing notification:', type);
      return;
    }

    // Emit to currentMessage for in-app handling (foreground banner, call
    // prompts, etc.). Subscribers also apply their own DND check, but the
    // gate above already filters out non-call payloads when DND is on.
    this.currentMessage.next(payload);

    // Check visibility and focus
    const isTabVisible = document.visibilityState === 'visible';
    const hasWindowFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;

    // Show browser notification if:
    // 1. Tab is not visible (user is on another tab or browser is minimized)
    // 2. It's not from the current user (except for calls which always notify)
    // 3. Browser supports notifications and permission is granted
    // 4. Not a popup window — only the main tab should trigger browser
    //    notifications so that duplicate system notifications are avoided
    //    when extra windows (cameras, chat pop-outs, etc.) are open.
    if ((!isTabVisible || !hasWindowFocus) && !isFromSelf && !this.isPopUpOpener && 'Notification' in window && Notification.permission === 'granted') {
      // console.log('[NotificationsHandlers] Showing background notification');
      this.showBackgroundNotification(payload);
    } else if ((!isTabVisible || !hasWindowFocus) && isFromSelf) {
      // console.log('[NotificationsHandlers] Skipping notification (from self)');
    }
  }


  private normalizeIncomingPayload(payload: any): any {
    if (!payload || typeof payload !== 'object') return payload;

    const data = payload?.data || payload;
    const type = payload?.type || data?.type;
    if (type !== 'chat') return payload;

    const normalized = payload?.data ? { ...payload, data: { ...payload.data } } : { ...payload };
    const d = normalized?.data || normalized;

    const isGeneralChat = d?.isGeneralChat === true || d?.isGeneralChat === 'true'
      || (d?.caseId && d?.receiverId && d?.caseId === d?.receiverId);
    const isGroup = d?.isGroup === true || d?.isGroup === 'true';

    if (isGeneralChat && d?.isGeneralChat !== 'true') {
      d.isGeneralChat = 'true';
    }

    if (isGroup && d?.isGroup !== 'true') {
      d.isGroup = 'true';
    }

    if (d?.isGroup === 'true') {
      if (!d.groupId && d.receiverId) d.groupId = d.receiverId;
      if (!d.groupName && d.receiverName) d.groupName = d.receiverName;
      if (!d.receiverId && d.groupId) d.receiverId = d.groupId;
      if (!d.receiverName && d.groupName) d.receiverName = d.groupName;
    }

    if (d?.isGeneralChat === 'true') {
      if (!d.receiverId && d.caseId) d.receiverId = d.caseId;
      if (!d.receiverName && d.caseName) d.receiverName = d.caseName;
    }

    return normalized;
  }

  /**
   * Request notification permission from the browser
   */
  private async requestNotificationPermission(): Promise<void> {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      console.log('Notification permission already granted');
      return;
    }

    if (Notification.permission !== 'denied') {
      try {
        const permission = await Notification.requestPermission();
        console.log('Notification permission:', permission);
      } catch (error) {
        console.error('Failed to request notification permission:', error);
      }
    }
  }

  /**
   * Returns true only if:
   * 1. The user is a member of the group
   * 2. The group belongs to the specified case
   * 
   * Both conditions must be met for the user to receive group chat messages.
   */
  private isCurrentUserInGroup(caseId: string, groupId: string): boolean {
    const currentUserId = this.requestService.currentUser?._id;
    if (!currentUserId || !caseId || !groupId) {
      return false;
    }

    // Check if we have group data loaded for this case
    const profile = this.caseService.profile?.getValue();
    const currentCaseId = profile?._id;

    // Groups in investigatorGroups belong to the currently loaded case
    // If we're not viewing the same case, we can't verify the group belongs to that case
    if (!currentCaseId || currentCaseId !== caseId) {
      return false;
    }

    // Check if the group exists in this case's groups
    const groups = this.caseService.investigatorGroups || [];
    const group = groups.find((g: any) => g._id === groupId);

    if (!group) {
      // Group not found in this case's groups - group doesn't belong to this case
      return false;
    }

    // Verify user is a member of the group
    const memberIds = group.investigatorIds || (group.investigators || []).map((i: any) => i._id);
    return memberIds.includes(currentUserId);
  }

  /**
   * Initialize NATS subscription for the current user.
   * Safe to call multiple times (e.g. from constructor, currentUserSubject, registerDevice):
   * only one initialization runs; concurrent callers await the same promise.
   */
  private async initializeNatsSubscription(): Promise<void> {
    const userId = this.requestService.getUserId();
    const userprefrecture = this.requestService.getUserPrefecture();
    if (!userId) return;

    // Already initialized: nothing to do
    if (this.natsSubscriptionActive) return;

    // Init already in progress: wait for it and return
    if (this.natsInitPromise !== null) {
      await this.natsInitPromise;
      return;
    }

    this.natsInitPromise = (async () => {
      try {
        this.connectionStatus.next('connecting');
        await this.natsMessaging.init();

        // Primary notifications subscription
        this.natsMessaging.subscribe('', `notify.user.${userId}`, async (payload) => {
          console.log(`initializeNatsSubscription notify.user.${userId}`, payload);
          await this.handleNatsMessage(payload);
        });

        this.natsMessaging.subscribe('', `notify.user.web.${userId}`, async (payload) => {
          console.log(`initializeNatsSubscription notify.web.${userId}`, payload);
          await this.handleNatsMessage(payload);
        });

        // Org-level web notifications (e.g. code version update broadcast).
        this.natsMessaging.subscribe('', 'notify.web', async (payload) => {
          console.log(`initializeNatsSubscription notify.web`, payload);
          await this.handleNatsMessage(payload);
        });

        // prefrecture-level web notifications (e.g. code version update broadcast).
        this.natsMessaging.subscribe('', `${userprefrecture}.notify.web`, async (payload) => {
          console.log(`initializeNatsSubscription ${userprefrecture}.notify.web`, payload);
          await this.handleNatsMessage(payload);
        });

        // Fallback: subscribe to all case updates and use chat messages as notifications
        // Only forward group chat to users who are members of that group
        // General chat is forwarded to all (backend handles case membership filtering)
        // this.natsMessaging.subscribe('', 'case.>', async (payload) => {
        //   const data = payload?.data || payload;
        //   const type = payload?.type || data?.type;
        //   if (type === 'chat') {
        //     const isGroup = data?.isGroup === true || data?.isGroup === 'true';
        //     const isGeneralChat = data?.caseId && data?.receiverId && data?.caseId === data?.receiverId;
        //     const caseId = data?.caseId;
        //     const groupId = data?.receiverId || data?.groupId;
        //     const currentUserId = this.requestService.currentUser?._id;

        //     // For specific group chats (not general), only forward if user is a member of that group
        //     if (isGroup && !isGeneralChat && caseId && groupId && currentUserId) {
        //       if (!this.isCurrentUserInGroup(caseId, groupId)) {
        //         return;
        //       }
        //     }

        //     // General chat messages are forwarded to all (no filtering by viewing)
        //     await this.handleNatsMessage(payload);
        //   }
        // });

        this.natsSubscriptionActive = true;
        console.log('NATS subscription initialized for user:', userId);
      } catch (error) {
        console.error('Failed to initialize NATS subscription:', error);
        this.connectionStatus.next('disconnected');
      } finally {
        this.natsInitPromise = null;
      }
    })();

    await this.natsInitPromise;
  }

  /**
   * Show a browser notification
   */
  private showBrowserNotification(payload: any): void {
    const data = payload?.data || payload;
    const type = data?.type || payload?.type;

    // Match SW gate: sync-only chat must not show a system notification (#1228).
    if (type === 'chat' && !data?.notificationGrade && !data?.cipher_timestamp) {
      return;
    }

    // Build title and body based on notification type
    let title = this.appTitle;
    let body = '';

    if (type === 'chat') {
      // Chat message: use sender name as title, content as body
      title = data?.senderName || 'New Message';
      body = previewChatContent(data?.content || data?.body || data?.message || '');
    } else if (type === 'call') {
      // Call notification — use the backend-provided title (e.g. "CIVIX") and
      // the already-localised message so the notification respects the user's
      // language setting.  Fall back to English literals only when the payload
      // does not carry these fields.
      title = data?.title || this.appTitle;
      body = data?.message || data?.body || 'You have an incoming call';
    } else {
      const caseId = data?.case_id || data?.caseId || data?.case?.case_id || '';
      if (type === 'annotation') {
        body = `${this.translate.instant('Annotation added to')}: ${caseId}`.trim();
      } else if (type === 'location') {
        const location = data?.location || '';
        body = (location ? `${this.translate.instant('Go to')} ${location}` : '').trim();
      } else {
        body = data?.body || data?.message || payload?.message || data?.content || data?.title || payload?.title || '';
      }

      // If sender name exists and not already in title, prepend to body
      if (data?.senderName && !title.includes(data.senderName)) {
        body = `${data.senderName}: ${body}`;
      }
    }

    const options: NotificationOptions = {
      body: body,
      icon: this.iconImageSrc,
      badge: this.badgeImageSrc,
      tag: `cipher-${type || 'notification'}-${Date.now()}`,
      data: { ...data, type },
      requireInteraction: type === 'call'
    };

    try {
      const notification = new Notification(title, options);
      if (type === 'call') {
        this.activeCallInviteNotifications.add(notification);
        notification.onclose = () => this.activeCallInviteNotifications.delete(notification);
      }

      notification.onclick = () => {
        window.focus();
        notification.close();
        this.markClickedNotificationAsRead(data);

        // For call notifications, don't navigate — just focus the window
        // and (re-)trigger the foreground call prompt so the user sees
        // Join / Decline buttons.  If they are already in a call the
        // prompt is suppressed and a toast is shown instead.
        if (type === 'call' && data?.invitedBy) {
          if (this.chatService.ongoingCall) {
            this.layoutUtilsService.showNotification(
              this.translate.instant('You are already in a call. Cannot join another call.'),
              this.translate.instant('Dismiss')
            );
          } else {
            data['show'] = true;
            this.chatService.showCallPromptSubject.next(data);
          }
          return;
        }

        // Navigate to appropriate page for non-call notifications
        if (type === 'chat') {
          const redirect = buildChatNotificationUrl(data, this.requestService.orgId);
          this.router.navigateByUrl(redirect);
        } else if (data?.caseId) {
          this.router.navigate([`/case/${data.caseId}`]);
        } else if (data?.senderId) {
          this.router.navigate([`/global-chat/${data.senderId}`]);
        } else {
          this.router.navigate(['/notifications']);
        }
      };
    } catch (error) {
      console.error('Failed to show notification:', error);
      // Fallback to service worker notification
      this.sendToServiceWorker(payload);
    }
  }

  /**
   * Re-initialize NATS subscription (e.g., after login or token refresh)
   */
  async reinitializeNatsSubscription(): Promise<void> {
    this.natsSubscriptionActive = false;
    this.natsInitPromise = null;
    await this.natsMessaging.disconnect();
    await this.initializeNatsSubscription();
  }

  /**
   * Register the current device with the backend
   */
  async registerDevice(): Promise<void> {
    if (this.isRegistered) return;

    const userId = this.requestService.getUserId();
    if (!userId) return;

    try {
      // Get or generate device ID
      this.token = this.deviceIdService.getDeviceId();
      this.tokenSubject.next(this.token);

      // Register with backend
      await this.registerToken(this.token);

      // Initialize NATS subscription
      await this.initializeNatsSubscription();

      console.log('Device registered:', this.token);
    } catch (error) {
      console.error('Device registration failed:', error);
    }
  }

  /**
   * Register the service worker for background notifications
   */
  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator) || this.isPopUpOpener) {
      console.log('Service Worker not supported or in popup');
      return;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/nats-sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered:', this.swRegistration.scope);

      // Handle service worker updates
      this.swRegistration.addEventListener('updatefound', () => {
        const newWorker = this.swRegistration?.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New service worker available');
            }
          });
        }
      });
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  /**
   * Send a notification to the service worker for background display
   */
  sendToServiceWorker(payload: any): void {
    if (this.swRegistration?.active) {
      this.swRegistration.active.postMessage({
        type: 'NATS_NOTIFICATION',
        payload: payload
      });
    }
  }

  /**
   * Prefer service worker for background notifications (Chrome system UI)
   */
  private showBackgroundNotification(payload: any): void {
    if (this.swRegistration?.active) {
      this.sendToServiceWorker(payload);
      return;
    }
    // Fallback to in-page Notification API if SW isn't ready
    this.showBrowserNotification(payload);
  }

  /**
   * Store notification locally for offline access
   */
  async storeNotification(payload: any): Promise<void> {
    const userId = this.requestService.getUserId();
    if (!userId) return;

    const notification = this.notificationStorage.payloadToNotification(payload, userId);
    await this.notificationStorage.storeNotification(notification);
  }

  /**
   * Get stored notifications from IndexedDB
   */
  async getStoredNotifications(limit: number = 100): Promise<any[]> {
    const userId = this.requestService.getUserId();
    if (!userId) return [];

    return await this.notificationStorage.getNotifications(userId, limit);
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const userId = this.requestService.getUserId();
    if (!userId) return 0;

    return await this.notificationStorage.getUnreadCount(userId);
  }

  sendNotificationToDevice(receiverId: string, payload: any): Promise<void> {
    return Promise.resolve();
  }

  sendNotificationToType(type: string, payload: any) {
    return;
  }

  subscribeTopic(topic: string, callback: any): Promise<void> {
    return Promise.resolve();
  }

  unsubscribeTopic(topic: string, callback: any): Promise<void> {
    return Promise.resolve();
  }

  unregisterDevice(userId: string, deviceId: string, callback: any, notValidToken: boolean = false): Promise<void> {
    this.isRegistered = false;
    if (callback) callback();
    return Promise.resolve();
  }

  unsubscribeTopics(itemName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let existingTopics = JSON.parse(localStorage.getItem(itemName)) || [];
      if (existingTopics?.length) {
        existingTopics.forEach(async topic => {
          await this.unsubscribeTopic(topic, undefined).then(() => {
            let arr = existingTopics;
            arr = arr.filter(i => i != topic);
            if (arr.length == 0)
              localStorage.removeItem(itemName);
            else
              localStorage.setItem(itemName, JSON.stringify(arr));
          });
        });
        // localStorage.removeItem(itemName);
        resolve();
      }
      else
        resolve();
    });
  }

  async logout(notValidToken: boolean = false) {
    this.loader.display(true);

    // Stop NATS and clear state so we don't reconnect or think we're still subscribed
    this.natsSubscriptionActive = false;
    this.natsInitPromise = null;
    this.connectionStatus.next('disconnected');

    // Reset all notification / call BehaviorSubjects so the next user who
    // logs in on the same browser does not inherit stale call invites or
    // notification payloads from the previous session.
    this.currentMessage.next(null);
    this.chatService.showCallPromptSubject.next({ show: false } as any);
    // this.chatService.showJitsiCallDialogSubject.next({ show: false } as any);
    this.chatService.ongoingCall = undefined;

    // Dismiss any browser notifications still visible in the system tray
    // so that the next user cannot click on the previous user's notifications.
    if (this.swRegistration) {
      try {
        const notifications = await this.swRegistration.getNotifications();
        notifications.forEach(n => n.close());
      } catch { /* ignore – SW may already be unregistered */ }
    }

    if (this.token) {
      this.isRegistered = false;
      let unregisterDevicePromise = this.unregisterDevice(this.requestService?._currentUser?._id, this.token, undefined, notValidToken);
      await Promise.all([unregisterDevicePromise]).then(async () => {
        await this.natsMessaging.disconnect();
        const userId = this.requestService.getUserId();
        if (userId) {
          await this.notificationStorage.clearAll(userId);
        }
        this.requestService.logOutApi();
        this.token = '';
        this.tokenSubject.next(this.token);
      }).catch(async () => {
        await this.natsMessaging.disconnect();
        const userId = this.requestService.getUserId();
        if (userId) {
          await this.notificationStorage.clearAll(userId);
        }
        this.requestService.logOutApi();
        this.token = '';
        this.tokenSubject.next(this.token);
      });
    } else {
      await this.natsMessaging.disconnect();
      const userId = this.requestService.getUserId();
      if (userId) {
        await this.notificationStorage.clearAll(userId);
      }
      this.requestService.logOutApi();
      this.token = '';
      this.tokenSubject.next(this.token);
    }
  }

  async sendMessageNotification(receiverId: string, senderId: string, body: string, title: string, type: any, name: string, actionId: string, receiverName: string, senderName: string, isGroup, caseId, caseName, case_id, groups) {
    try {

      if (isGroup) {
        const isGeneralGroup = !!caseId && receiverId === caseId;
        if (isGeneralGroup) {
          // console.log('General chat');
          this.requestService.postRequest('notify', `case/${receiverId}/send`, {
            // deviceToken: receiverToken,
            // message: title + ': ' + body,
            // title: 'CIPHER',
            payload: {
              deviceToken: [],
              message: body,
              title: this.appTitle,
              data: {
                type: type,
                actionId: actionId.toString(),
                senderId: senderId,
                senderName: senderName,
                receiverName: receiverName,
                receiverId: receiverId,
                title: title,
                body: body,
                caseId: caseId,
                isGeneralChat: 'true'
              }
            }
          }, (data, error) => {

          });

        } else {

          this.requestService.postRequest('notify', `group/${receiverId}/send`, {
            // topic: `chat-group-${receiverId}`,
            payload: {
              notification: {
                body: body,
                title: this.appTitle,
                // click_action: '/case/' + caseId + '/chat/group/' + receiverId,
                // icon: 'assets/images/logo-small.png',
              },
              data: {
                type: type,
                actionId: actionId.toString(),
                senderName: senderName,
                receiverName: receiverName,
                senderId: senderId,
                receiverId: receiverId,
                title: title,
                body: body,
                caseId: caseId,
                groupId: receiverId,
                groupName: receiverName,
                isGroup: 'true',
              }
            }
          }, (data, error) => {

          });

        }
      }
      //send notifications to user
      else if (receiverId) {
        // if (!receiverToken.web_device_tokens)
        //   receiverToken.web_device_tokens = [];
        // if (!receiverToken.android_device_tokens)
        //   receiverToken.android_device_tokens = [];
        // if (!receiverToken.ios_device_tokens)
        //   receiverToken.ios_device_tokens = [];
        // let TokensArray = receiverToken.web_device_tokens.concat(receiverToken.ios_device_tokens, receiverToken.android_device_tokens);

        // let notificationData = {
        //   "message": body,
        //   "title": title,
        //   "deviceToken": receiverToken,
        //   "data": {
        //     actionType: type,
        //     actionId: actionId.toString(),
        //     senderName: senderName,
        //     receiverName: receiverName,
        //     title: title,
        //     body: body
        //   }
        // }

        this.requestService.postRequest('notify', `push/${receiverId}/send`, {
          // deviceToken: receiverToken,
          // message: title + ': ' + body,
          // title: 'CIPHER',
          payload: {
            deviceToken: [],
            message: body,
            title: this.appTitle,
            data: {
              type: type,
              actionId: actionId.toString(),
              senderId: senderId,
              senderName: senderName,
              receiverName: receiverName,
              title: title,
              body: body,
              caseId: caseId,
            }
          }
        }, (data, error) => {

        });
      }
    } catch (err) {
      console.log(err);
      throw new Error(err.message)
    }
  }

  uploadBytesResumable(senderId, receiverId, imageId, file, type, actionId, receiverName, senderName, channel, isGroup, caseId, doNotify, caseName, case_id, groupMembers, isGeneralChat): Promise<any> {
    return new Promise((resolve, reject) => {
      this.progressRef.next(0);

      const formData = new FormData();
      formData.append('files', file);

      let token = '';
      if (this.requestService.currentUser && this.requestService.currentUser.token) {
        token = this.requestService.currentUser.token;
      } else {
        const stored = localStorage.getItem('currentUser');
        if (stored) {
          token = JSON.parse(stored).token;
        }
      }

      const headers = new HttpHeaders({
        'Authorization': token,
        'project': environment.projectName || 'cipher',
        'organizationid': environment.orgId,
        'productid': environment.productId || ''
      });

      this.http.post(environment.serverUrl + 'chat/upload', formData, {
        headers: headers,
        reportProgress: true,
        observe: 'events'
      }).subscribe(async (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          const percentDone = Math.round(100 * event.loaded / event.total);
          this.progressRef.next(percentDone);
        } else if (event.type === HttpEventType.Response) {
          if (event.body && event.body.status && event.body.results && event.body.results.url) {
            const fileUrl = event.body.results.url;
            try {
              const msgData = await this.sendMessage(
                senderId,
                senderName,
                receiverId,
                receiverName,
                '',
                caseId,
                'chat',
                file.name,
                fileUrl,
                isGroup,
                isGeneralChat,
                undefined
              );
              resolve(msgData);
            } catch (err) {
              reject(err);
            }
          } else {
            reject('Upload failed: Invalid response');
          }
        }
      }, error => {
        console.error('Upload error:', error);
        reject(error);
      });
    });
  }

  registerToken(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isRegistered && this.requestService.currentUser?._id && token) {
        this.isRegistered = true;
        this.requestService.postRequest('notify', 'register', {
          userId: this.requestService.currentUser._id,
          type: "Web",
          deviceId: token
        }, (data, error) => {
          // console.log('registerToken', data, error);
          resolve();
        });
      }
      else {
        resolve();
      }
    });
  }

  getUserToken(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('user', 'search', {
        order: [],
        fields: ["name", "alternative_name", "deviceids"],
        term: '',
        filter: {
          "$and": [{ "organizationId._id": { "$in": [this.requestService.orgId] } }, { "_id": { "$eq": userId } }]
        }
      }, (data, error) => {
        if (data?.results?.length) {
          resolve(data.results);
        }
        else if (error) {
          reject(error);
        }
        else {
          reject();
        }
      });
    });
  }

  sendCaseNotification(caseId: string, senderId: string, body: string, title: string, type: any, actionId: string, receiverName: string, senderName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('notify', 'case/' + caseId + '/send', {
        payload: {
          deviceToken: [],
          message: title + ': ' + body,
          title: this.appTitle,
          data: {
            type: type,
            actionId: actionId.toString(),
            senderId: senderId,
            senderName: senderName,
            receiverName: receiverName,
            title: title,
            body: body,
            caseId: caseId,
          }
        }
      }, (data, error) => {
        if (data)
          resolve(data);
        if (error)
          reject(error);
      });
    });
  }

  sendMessage(senderId, senderName, receiverId, receiverName, content, caseId, type, fileName?, fileUrl?, isGroup: boolean = false, isGeneralChat: boolean = false, clientMessageId?: string, sourceCaseId?: string, isOrgLevelMessageAll: boolean = false) {
    const msgId = clientMessageId || (globalThis.crypto && 'randomUUID' in globalThis.crypto ? (globalThis.crypto as any).randomUUID() : `${Date.now()}_${Math.random()}`);
    const payload = {
      senderId: senderId,
      senderName: senderName,
      receiverId: receiverId,
      receiverName: receiverName,
      content: content,
      caseId: caseId,
      type: type,
      fileName: fileName,
      fileUrl: fileUrl,
      isGroup: isGroup,
      isGeneralChat: isGeneralChat,
      isOrgLevelMessageAll: isOrgLevelMessageAll,
      sourceCaseId: sourceCaseId,
      clientInstanceId: this.clientInstanceId,
      clientMessageId: msgId
    };

    return new Promise((resolve, reject) => {
      this.requestService.postRequest('chat', 'send', payload, (data, error) => {
        if (data) {
          // Also trigger notification flow if needed, or rely on NATS in backend
          // this.sendNotification(...) 
          if (type === 'chat') {
            this.chatService.handleSentMessage({
              isGroup: isGroup,
              isGeneralChat: isGeneralChat,
              caseId: caseId,
              receiverId: receiverId,
              createdAt: new Date().toISOString(),
              content: content,
              fileName: fileName,
              fileUrl: fileUrl,
              ...data
            });
          }
          resolve(data);
        } else {
          reject(error);
        }
      });
    });
  }
}
